import React, { useContext } from 'react';
import { FormContext } from '../../context/EquipmentFormContext';
import { LuCpu } from 'react-icons/lu';
import { FiCalendar } from 'react-icons/fi';

// ============================================================
// DROPDOWN OPTIONS
// ============================================================

const operationalStatusOptions = [
  'In Service',
  'Out of Service',
  'Under Maintenance',
  'Awaiting Repair',
  'Decommissioned',
];

const conditionOptions = [
  'Excellent',
  'Good',
  'Fair',
  'Poor',
  'Critical',
];

const riskLevelOptions = [
  'Low',
  'Medium',
  'High',
  'Critical',
];

const foodSafetyOptions = [
  'Yes - Affects food safety',
  'No - Does not affect food safety',
  'Indirectly affects food safety',
];

export default function Step3() {
  const { formData, updateField } = useContext(FormContext);

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">

      {/* ========================================================
          HEADER
      ======================================================== */}

      <div className="flex items-start space-x-4 mb-6">

        <div className="p-2 bg-blue-600 rounded-lg text-white">
          <LuCpu className="w-5 h-5 stroke-[2.5]" />
        </div>

        <div>

          <div className="flex items-center space-x-2">

            <span className="text-xs font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              3
            </span>

            <h2 className="text-base font-bold text-indigo-950">
              Operational Status & Criticality
            </h2>

          </div>

          <p className="text-xs text-slate-500 mt-0.5">
            Current operational status and importance to your business.
          </p>

        </div>

      </div>

      {/* ========================================================
          STATUS / CRITICALITY
      ======================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

        {/* ======================================================
            OPERATIONAL STATUS
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Operational Status{' '}
            <span className="text-red-500">*</span>
          </label>

          <select
            value={formData.operationalStatus}
            onChange={(e) =>
              updateField(
                'operationalStatus',
                e.target.value
              )
            }
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
          >

            {operationalStatusOptions.map((status) => (
              <option
                key={status}
                value={status}
              >
                {status}
              </option>
            ))}

          </select>

        </div>

        {/* ======================================================
            CONDITION
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Condition
          </label>

          <select
            value={formData.condition}
            onChange={(e) =>
              updateField(
                'condition',
                e.target.value
              )
            }
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
          >

            {conditionOptions.map((condition) => (
              <option
                key={condition}
                value={condition}
              >
                {condition}
              </option>
            ))}

          </select>

        </div>

        {/* ======================================================
            RISK LEVEL
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Risk / Criticality Level{' '}
            <span className="text-red-500">*</span>
          </label>

          <select
            value={formData.riskLevel}
            onChange={(e) =>
              updateField(
                'riskLevel',
                e.target.value
              )
            }
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
          >

            {riskLevelOptions.map((risk) => (
              <option
                key={risk}
                value={risk}
              >
                {risk}
              </option>
            ))}

          </select>

        </div>

        {/* ======================================================
            FOOD SAFETY
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Food Safety Relevance{' '}
            <span className="text-red-500">*</span>
          </label>

          <select
            value={formData.foodSafetyRelevance}
            onChange={(e) =>
              updateField(
                'foodSafetyRelevance',
                e.target.value
              )
            }
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
          >

            {foodSafetyOptions.map((option) => (
              <option
                key={option}
                value={option}
              >
                {option}
              </option>
            ))}

          </select>

        </div>

      </div>

      {/* ========================================================
          DATES / LIFESPAN
      ======================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">

        {/* ======================================================
            DATE BROUGHT INTO SERVICE
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Date Brought Into Service
          </label>

          <div className="relative">

            <input
              type="text"
              value={formData.dateInService}
              onChange={(e) =>
                updateField(
                  'dateInService',
                  e.target.value
                )
              }
              placeholder="DD MMM YYYY"
              className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />

            <FiCalendar className="w-4 h-4 text-slate-400 absolute left-3 top-3" />

          </div>

        </div>

        {/* ======================================================
            EXPECTED LIFESPAN
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Expected Lifespan (Years)
          </label>

          <input
            type="text"
            value={formData.expectedLifespan}
            onChange={(e) =>
              updateField(
                'expectedLifespan',
                e.target.value
              )
            }
            placeholder="e.g. 5"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />

        </div>

        {/* ======================================================
            INITIAL INSPECTION DATE
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Initial Inspection / Commissioning Date
          </label>

          <div className="relative">

            <input
              type="text"
              value={formData.initialInspectionDate}
              onChange={(e) =>
                updateField(
                  'initialInspectionDate',
                  e.target.value
                )
              }
              placeholder="DD MMM YYYY"
              className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />

            <FiCalendar className="w-4 h-4 text-slate-400 absolute left-3 top-3" />

          </div>

        </div>

      </div>

    </div>
  );
}